<template>
  <div class="field">
    <label class="field__label" :for="id">
      {{ label }}
      <span v-if="optional" class="field__optional">{{ optionalLabel }}</span>
    </label>

    <div class="field__control">
      <input
        :id="id"
        ref="input"
        class="field__input"
        :class="{ 'has-error': !!error, 'has-toggle': isPassword }"
        :type="resolvedType"
        :value="modelValue"
        :placeholder="placeholder"
        :required="required"
        :autocomplete="autocomplete"
        :aria-invalid="error ? 'true' : undefined"
        :aria-describedby="error ? `${id}-error` : (hint ? `${id}-hint` : undefined)"
        @input="$emit('update:modelValue', $event.target.value)"
      />
      <button
        v-if="isPassword"
        class="field__toggle"
        type="button"
        :aria-label="revealed ? hidePasswordLabel : showPasswordLabel"
        :aria-pressed="revealed ? 'true' : 'false'"
        @click="revealed = !revealed"
      >
        <i :class="revealed ? 'fas fa-eye-slash' : 'fas fa-eye'" aria-hidden="true"></i>
      </button>
    </div>

    <p v-if="error" :id="`${id}-error`" class="field__error">{{ error }}</p>
    <p v-else-if="hint" :id="`${id}-hint`" class="field__hint">{{ hint }}</p>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { nextUid } from '@/utils/uid';

/*
 * The auth inputs had labels but no autocomplete, no error wiring, no
 * password reveal and no working focus ring — the outline referenced
 * --cyber-cyan, which was declared only inside scoped :root blocks and so
 * never resolved.
 */
const props = defineProps({
  modelValue: { type: String, default: '' },
  label: { type: String, required: true },
  type: { type: String, default: 'text' },
  placeholder: { type: String, default: '' },
  autocomplete: { type: String, default: undefined },
  required: Boolean,
  optional: Boolean,
  optionalLabel: { type: String, default: '' },
  error: { type: String, default: '' },
  hint: { type: String, default: '' },
  showPasswordLabel: { type: String, default: 'Show password' },
  hidePasswordLabel: { type: String, default: 'Hide password' },
});

defineEmits(['update:modelValue']);

const id = nextUid('field');
const revealed = ref(false);
const isPassword = computed(() => props.type === 'password');
const resolvedType = computed(() =>
  isPassword.value ? (revealed.value ? 'text' : 'password') : props.type
);
</script>

<style scoped>
.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.field__label {
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--text);
}

.field__optional {
  color: var(--text-faint);
  font-weight: var(--weight-regular);
}

.field__control {
  position: relative;
  display: flex;
}

.field__input {
  width: 100%;
  padding: 0.7rem 0.9rem;
  background: var(--surface-0);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  color: var(--text);
  font-size: var(--text-base);
  transition: border-color var(--duration) var(--ease), box-shadow var(--duration) var(--ease);
}

.field__input.has-toggle {
  padding-right: 2.75rem;
}

.field__input:hover {
  border-color: var(--border-bevel);
}

.field__input:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-wash);
}

.field__input.has-error {
  border-color: var(--danger);
}

.field__toggle {
  position: absolute;
  right: 0.25rem;
  top: 50%;
  transform: translateY(-50%);
  width: 2.25rem;
  height: 2.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 0;
  color: var(--text-muted);
  cursor: pointer;
}

.field__toggle:hover {
  color: var(--accent);
}

.field__error {
  font-size: var(--text-sm);
  color: var(--danger);
  margin: 0;
}

.field__hint {
  font-size: var(--text-sm);
  color: var(--text-faint);
  margin: 0;
}
</style>
